import React, { useState } from 'react';
import { ShoppingCart, CheckCircle, XCircle } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { procurementService } from '../../../../services/procurementService';
import type {
  Department,
  ProcurementOrderLineDraft,
} from '../../../../types/procurement';
import { OrderHeaderStep } from './OrderHeaderStep';
import { OrderLinesStep } from './OrderLinesStep';

interface BestillingssystemMainProps {
  onBack?: () => void;
}

type Step = 1 | 2;

export const BestillingssystemMain: React.FC<BestillingssystemMainProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [legalEntityNo, setLegalEntityNo] = useState('');
  const [legalEntityName, setLegalEntityName] = useState('');
  const [department, setDepartment] = useState<Department | null>(null);
  const [lines, setLines] = useState<ProcurementOrderLineDraft[]>([]);
  const [flash, setFlash] = useState<
    | { kind: 'success'; text: string }
    | { kind: 'error'; text: string }
    | null
  >(null);
  const [submitting, setSubmitting] = useState(false);

  const showFlash = (kind: 'success' | 'error', text: string) => {
    setFlash({ kind, text });
    setTimeout(() => setFlash(null), 5000);
  };

  const resetAll = () => {
    setStep(1);
    setLegalEntityNo('');
    setLegalEntityName('');
    setDepartment(null);
    setLines([]);
  };

  const handleProceedToLines = (
    selectedLegalEntityNo: string,
    selectedLegalEntityName: string,
    selectedDepartment: Department
  ) => {
    setLegalEntityNo(selectedLegalEntityNo);
    setLegalEntityName(selectedLegalEntityName);
    setDepartment(selectedDepartment);
    setLines([]);
    setStep(2);
  };

  const handleFinish = async () => {
    if (!department || lines.length === 0) return;
    try {
      setSubmitting(true);
      const response = await procurementService.createOrderWithLines({
        legalEntityNo,
        departmentNo: department.departmentNo,
        lines: lines.map((l) => ({ itemNo: l.itemNo, quantity: l.quantity, unit: l.unit })),
      });
      showFlash(
        'success',
        `Bestilling ${response.header.orderNo} er opprettet med ${response.lines.length} linje(r).`
      );
      resetAll();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ukjent feil';
      showFlash('error', `Kunne ikke opprette bestilling: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelOrder = () => {
    resetAll();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <BackButton onClick={onBack || (() => window.history.back())} />
          <ShoppingCart className="text-blue-600" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Bestillingssystem</h1>
            <p className="text-sm text-gray-600 mt-1">
              Legg inn bestilling fra avdeling til sentrallager
            </p>
          </div>
        </div>
      </div>

      {flash && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div
            className={`rounded-lg p-4 flex items-center gap-3 border ${
              flash.kind === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {flash.kind === 'success' ? (
              <CheckCircle className="flex-shrink-0" size={22} />
            ) : (
              <XCircle className="flex-shrink-0" size={22} />
            )}
            <div className="font-medium">{flash.text}</div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Step indicator */}
        <div className="mb-6 flex items-center gap-2 text-sm">
          <StepBadge active={step === 1} done={step > 1} label="1. Velg avdeling" />
          <div className="h-px flex-1 bg-gray-300" />
          <StepBadge active={step === 2} done={false} label="2. Legg inn linjer" />
        </div>

        {step === 1 && (
          <OrderHeaderStep
            operatorName={user?.operatorName || user?.operatorId || ''}
            onProceed={handleProceedToLines}
          />
        )}

        {step === 2 && department && (
          <OrderLinesStep
            legalEntityNo={legalEntityNo}
            legalEntityName={legalEntityName}
            department={department}
            operatorName={user?.operatorName || user?.operatorId || ''}
            lines={lines}
            onLinesChange={setLines}
            onFinish={handleFinish}
            onCancel={handleCancelOrder}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  );
};

interface StepBadgeProps {
  active: boolean;
  done: boolean;
  label: string;
}

const StepBadge: React.FC<StepBadgeProps> = ({ active, done, label }) => {
  const color = done
    ? 'bg-green-600 text-white'
    : active
    ? 'bg-blue-600 text-white'
    : 'bg-gray-200 text-gray-600';
  return (
    <div className={`px-3 py-1.5 rounded-full font-medium ${color}`}>{label}</div>
  );
};
